import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import styled, { ThemeContext } from 'styled-components/macro'
import { useTranslation } from 'react-i18next'
import Text from 'components/atoms/text'
import Typography from 'components/molecules/typography'
import { NLStrip, NLLabel } from 'components/atoms/license-plate'
import { EuStars } from 'components/atoms/eu-stars'

const PlateContainer = styled.div`
  position: relative;
  display: flex;
  background-color: ${({ theme }) => theme.colors.licensePlate};
  font-family: ${({ theme }) => theme.licensePlateFont || theme.font};
  border: 1px solid ${({ theme }) => theme.colors.text};
  border-radius: ${({ theme }) => theme.sizeByFactor(1.05)};
  overflow: hidden;
  font-size: ${({ theme }) => theme.sizeByFactor(3.5)};
  padding-left: ${({ theme }) => theme.sizeByFactor(4.9)};
`

const InputNLStrip = styled(NLStrip)`
  width: ${({ theme }) => theme.sizeByFactor(4.9)};
  padding: ${({ theme }) => theme.sizeByFactor(0.35)} 0 !important;
  gap: ${({ theme }) => theme.sizeByFactor(0.35)};
`

const InputNLLabel = styled(NLLabel)`
  font-size: ${({ theme }) => theme.sizeByFactor(1.33)};
`

const Input = styled(Text)`
  display: block;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.licensePlate};
  border: none;
  outline: none;
  padding: 8px 0;
  text-align: center;
  font-family: inherit;
  ::placeholder {
    color: ${({ theme }) => theme.colors.licensePlatePlaceholder};
  }
`

const Error = styled(Typography)`
  display: block;
  margin-left: 12px;
  margin-top: ${({ theme }) => theme.sizings.lvl0};
`

/**
 * TODO: LicensePlate internally uses the deprecated Text component (which renders an input element).
 * Replace with a proper field component instead.
 */
function LicensePlateInput({ placeholder, error, ...restProps }) {
  const { t } = useTranslation()
  const theme = useContext(ThemeContext)
  const showNLStrip = Boolean(theme?.licensePlateFont)

  const inputElement = (
    <Input
      color="text"
      tag="input"
      placeholder={placeholder || t('licensePlate')}
      data-test-e2e="license-plate-input"
      {...restProps}
      type="h1" // type can be fed down from ComplexForm. This ensures that it will not be overruled.
    />
  )

  return (
    // The div wraps the error message and the field (like NewTextInput)
    // so the spacing between them cannot be affected by
    // external layout logic, which will mess up its internal layout.
    <div>
      {showNLStrip ? (
        <PlateContainer>
          <InputNLStrip aria-hidden="true">
            <EuStars size="large" />
            <InputNLLabel>NL</InputNLLabel>
          </InputNLStrip>
          {inputElement}
        </PlateContainer>
      ) : (
        inputElement
      )}
      {typeof error === 'string' && <Error type="ErrorText">{error}</Error>}
    </div>
  )
}

LicensePlateInput.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  readOnly: PropTypes.bool,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
}

LicensePlateInput.defaultProps = {
  className: null,
  disabled: null,
  name: null,
  onChange: null,
  onBlur: null,
  readOnly: false,
  value: '',
  placeholder: null,
  error: false,
}

export default LicensePlateInput
